import React from 'react';
import {
  AdminPaymentRequestDetail,
  AdminPaymentRequestLine,
} from '../../api/requests';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { useToast } from '../../hooks/useToast';

const formatNaira = (n: number) =>
  `₦${n.toLocaleString('en-NG', { maximumFractionDigits: 0 })}`;

const formatDateTime = (s: string | null) =>
  s
    ? new Date(s).toLocaleString('en-NG', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const WA_LABEL: Record<
  string,
  { label: string; variant: 'success' | 'info' | 'neutral' | 'danger' }
> = {
  sent: { label: 'WhatsApp sent', variant: 'neutral' },
  delivered: { label: 'WhatsApp delivered', variant: 'info' },
  read: { label: 'WhatsApp read', variant: 'success' },
  failed: { label: "WhatsApp didn't deliver", variant: 'danger' },
};

const statusColors: Record<
  string,
  'success' | 'warning' | 'danger' | 'neutral' | 'info'
> = {
  open: 'info',
  partly_paid: 'warning',
  settled: 'success',
  expired: 'neutral',
  cancelled: 'danger',
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  color: 'var(--neutral-500)',
  marginBottom: 2,
};

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <div style={labelStyle}>{label}</div>
    <div style={{ color: 'var(--neutral-800)', fontSize: 14 }}>{children}</div>
  </div>
);

const PayeeCard: React.FC<{
  line: AdminPaymentRequestLine;
  onCopy: (url: string) => void;
}> = ({ line, onCopy }) => {
  const wa = line.waStatus ? WA_LABEL[line.waStatus] : null;
  return (
    <div
      style={{
        border: '1px solid var(--neutral-200)',
        borderRadius: 'var(--radius-md)',
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: 'var(--neutral-800)' }}>
            {line.payeeLabel}
            {line.isPayiqUser ? (
              <span style={{ marginLeft: 6 }}>
                <Badge variant="info">PayIQ user</Badge>
              </span>
            ) : null}
          </div>
          <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
            {line.payeePhone || line.payeeEmail || 'No contact on file'}
          </div>
        </div>
        <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          <div style={{ fontWeight: 600 }}>{formatNaira(line.amount)}</div>
          <Badge variant={line.status === 'paid' ? 'success' : 'warning'}>
            {line.status === 'paid' ? 'Paid' : 'Pending'}
          </Badge>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {wa ? <Badge variant={wa.variant}>{wa.label}</Badge> : null}
        {line.paidAt ? (
          <span style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
            Paid {formatDateTime(line.paidAt)}
          </span>
        ) : null}
        {line.status !== 'paid' ? (
          <Button size="sm" variant="ghost" onClick={() => onCopy(line.payUrl)}>
            Copy pay link
          </Button>
        ) : null}
      </div>
    </div>
  );
};

export const RequestDetailDrawer: React.FC<{
  detail: AdminPaymentRequestDetail;
}> = ({ detail }) => {
  const toast = useToast();

  const copy = (url: string) => {
    void navigator.clipboard?.writeText(url);
    toast.success('Pay link copied');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Summary */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 14,
        }}
      >
        <Row label="Status">
          <Badge variant={statusColors[detail.status] ?? 'neutral'}>
            {detail.status.replace('_', ' ')}
          </Badge>
        </Row>
        <Row label="Type">
          <span style={{ textTransform: 'capitalize' }}>{detail.type}</span>
        </Row>
        <Row label="Collected">
          {formatNaira(detail.paidAmount)} / {formatNaira(detail.totalAmount)}
          <span style={{ color: 'var(--neutral-500)', fontSize: 12 }}>
            {' '}
            ({detail.paidCount}/{detail.lineCount} paid)
          </span>
        </Row>
        <Row label="Reminders">
          {detail.remindCount}× · last {formatDateTime(detail.lastRemindedAt)}
        </Row>
        <Row label="Created">{formatDateTime(detail.createdAt)}</Row>
        <Row label="Expires">{formatDateTime(detail.expiresAt)}</Row>
      </div>

      {detail.note ? (
        <Row label="Note">{detail.note}</Row>
      ) : null}

      {detail.creator ? (
        <Row label="Requested by">
          {detail.creator.name}
          {detail.creator.email ? (
            <span style={{ color: 'var(--neutral-500)' }}> · {detail.creator.email}</span>
          ) : null}
        </Row>
      ) : null}

      {/* Payees */}
      <div>
        <div style={{ ...labelStyle, marginBottom: 8 }}>
          Payees ({detail.lines.length})
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {detail.lines.map((line) => (
            <PayeeCard key={line.id} line={line} onCopy={copy} />
          ))}
        </div>
      </div>
    </div>
  );
};
